import { Box, sanitizeToNumeric } from 'vopay';
import { Title, Button, UtilButton } from 'UI';
import { EMPTY_TIER, type ApprovalTier } from './ApprovalTiers/ApprovalTiers';
import useDeleteWorkflow from '@/hooks/useApi/useDeleteWorkflow';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDataFetchIndicator } from '@/hooks';
import { useHistory, useParams } from 'react-router-dom';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import usePostCreateWorkflow from '@/hooks/useApi/usePostCreateWorkflow';
import EditWorkflowForm from './EditWorkflowForm';
import type { UpdateWorkflow, UpdateWorkflowContainer } from './types';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import styles from './WorkflowContainer.module.scss';

type FC = FunctionComponent;

const EditWorkflowContainer: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [isReview, setIsReview] = useState(false);
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [workflowsList, setWorkFlowList] = useState<(UpdateWorkflowContainer | undefined)[]>([undefined]);
  const [, , updateWorkflowAsync] = usePostUpdateWorkflow({});
  const [, , createWorkFlowAsync] = usePostCreateWorkflow({});
  const [, , deleteWorkflowAsync] = useDeleteWorkflow({});
  const { approvalListReset, transactionsReset } = useApprovalWorkflow();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const alertDialog = useAlertDialog();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const {
    response: { Country },
  } = useClientData();

  const resetWorkflowList = () => {
    const newWorkflow = workflows
      .map((group) => {
        const sortedWorkflows = [...group.Workflows].sort((a, b) => parseFloat(a.MinAmount) - parseFloat(b.MinAmount));
        const tiers: ApprovalTier[] = sortedWorkflows.map((wf) => ({
          workflowId: wf.WorkflowID,
          startAmount: wf.MinAmount,
          endAmount: wf.MaxAmount ?? '',
          noUpperLimit: wf.MaxAmount === null,
          approversNeeded: wf.NumberOfApprovalsRequired.toString(),
        }));
        return {
          values: {
            ...group.Workflows[0],
            TransactionTypes: [group.TransactionType],
            tiers: tiers.length > 0 ? tiers : [{ ...EMPTY_TIER }],
          },
        };
      })
      .filter((workflow) => workflow.values.WorkflowID?.toString() === id);

    setWorkFlowList(newWorkflow);
  };

  useEffect(() => {
    resetWorkflowList();
  }, [workflows, id, transactionTypesResponse]);

  useEffect(() => {
    fetchWorkflows().catch(() => alertDialog.error(tGlobal('error'), tGlobal('errorOccurred')));
    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  const updateWorkflows = async () => {
    setDataFetchingInfo(true, 'loading some data', 'fullScreen');
    try {
      const workflow = workflowsList[0];
      if (!workflow) return;

      const transactionType = workflow.values.TransactionTypes?.[0] || '';
      const tiers = workflow.values.tiers?.filter((tier) => tier.approversNeeded !== '') || [];

      const originalGroup = workflows.find((group) => group.Workflows[0]?.WorkflowID?.toString() === id);
      const originalWorkflowIds = new Set(originalGroup?.Workflows.map((wf) => wf.WorkflowID) || []);
      const updatedWorkflowIds = new Set(tiers.filter((t) => t.workflowId).map((t) => t.workflowId!));

      for (const originalId of originalWorkflowIds) {
        if (!updatedWorkflowIds.has(originalId)) {
          const res = await deleteWorkflowAsync({ WorkflowID: originalId });
          if (!res.Success) throw Error(res.ErrorMessage);
        }
      }

      const originalWorkflows = originalGroup?.Workflows ?? [];
      const tiersToUpdate = [...tiers.filter((tier) => tier.workflowId)].sort((a, b) => {
        const aNew = parseFloat(sanitizeToNumeric(a.startAmount || '0') || '0');
        const bNew = parseFloat(sanitizeToNumeric(b.startAmount || '0') || '0');
        const aOrig = parseFloat(originalWorkflows.find((wf) => wf.WorkflowID === a.workflowId)?.MinAmount ?? '0');
        const bOrig = parseFloat(originalWorkflows.find((wf) => wf.WorkflowID === b.workflowId)?.MinAmount ?? '0');
        // Tiers shifting up must be processed highest-first so their old lower boundary
        // is still in place when a neighbour's new higher boundary is written.
        // Tiers shifting down must be processed lowest-first for the same reason in reverse.
        const aMovingUp = aNew >= aOrig;
        const bMovingUp = bNew >= bOrig;
        if (aMovingUp !== bMovingUp) return aMovingUp ? -1 : 1; // up-movers before down-movers
        return aMovingUp ? bNew - aNew : aNew - bNew;           // up: highest first; down: lowest first
      });
      const tiersToCreate = tiers.filter((tier) => !tier.workflowId);

      for (const tier of tiersToUpdate) {
        const res = await updateWorkflowAsync({
          WorkflowID: tier.workflowId!,
          NumberApprovalsRequired: sanitizeToNumeric(tier.approversNeeded) || '0',
          MinAmount: sanitizeToNumeric(tier.startAmount || '0') || '0',
          MaxAmount: tier.noUpperLimit ? null : sanitizeToNumeric(tier.endAmount),
        });
        if (!res.Success) throw Error(res.ErrorMessage);
      }

      for (const tier of tiersToCreate) {
        const res = await createWorkFlowAsync({
          TransactionType: transactionType,
          NumberApprovalsRequired: sanitizeToNumeric(tier.approversNeeded) || '0',
          MinAmount: sanitizeToNumeric(tier.startAmount || '0') || '0',
          MaxAmount: tier.noUpperLimit ? null : sanitizeToNumeric(tier.endAmount),
        });
        if (!res.Success) throw Error(res.ErrorMessage);
      }

      transactionsReset();
      approvalListReset();
      history.push({
        pathname: '/transaction-approval/configuration/workflows',
        state: {
          isCreate: false,
          hasCreateMultiple: false,
          isUpdate: true,
          numberCreated: 1,
          paymentRailName: transactionType,
        },
      });
    } catch (err) {
      alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
    } finally {
      setDataFetchingInfo(false);
    }
  };

  const handleFormChange = (FormId: number, values: UpdateWorkflow, isValid: boolean) => {
    const workflowListNew = [...workflowsList];
    workflowListNew[FormId] = { values: { ...values }, isValid };
    setWorkFlowList(workflowListNew);
  };

  return (
    <div className={styles.page}>
      <Box flx="col" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/transaction-approval/configuration/workflows');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
        </UtilButton>

        <Title level="1">
          {isReview
            ? t('reviewTitle', { defaultValue: 'Review Workflow' })
            : t('editTitle', { defaultValue: 'Edit Workflow' })}
        </Title>
      </Box>
      <div className={styles.formWrapper}>
        <EditWorkflowForm
          id={0}
          defaultValues={workflowsList[0]?.values}
          isReview={isReview}
          onChange={handleFormChange}
        />
      </div>
      <Box jc="fe">
        <Box gapSize="12px">
          {isReview && (
            <Button
              id="approval-edit-review"
              colorScheme="secondary"
              iconLeftProps={{ icon: 'edit', fillAmount: 1 }}
              onClick={() => setIsReview(false)}
            >
              {t('Buttons.edit', { defaultValue: 'Edit' })}
            </Button>
          )}
          <Button
            id="approval-save-workflow"
            disabled={workflowsList.some((workflow) => !workflow?.isValid)}
            colorScheme="primary"
            onClick={() => {
              if (!isReview) {
                setIsReview(true);
              } else {
                updateWorkflows();
              }
            }}
          >
            {isReview
              ? t('Buttons.updateWorkflow', { defaultValue: 'Update Workflow' })
              : t('Buttons.review', { defaultValue: 'Review' })}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default EditWorkflowContainer;
